/**
 * Helpers for the resort and loan term configuration returned by the Monera API
 */

const sameText = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * Find a resort by its name or code, ignoring case
 * @param {object[]} resorts Resorts from the API config
 * @param {string} value Resort name or code
 * @returns {object|null}
 */
export const findResort = (resorts, value) => {
  if (!value) {
    return null;
  }

  return resorts.find(({ name, code }) => sameText(name, value) || sameText(code, value)) || null;
};

/**
 * Resorts that can be quoted online for the contract type, sorted by name
 * @param {object[]} resorts Resorts from the API config
 * @param {string} contractType Either 'new' or 'refinance'
 * @returns {object[]}
 */
export const getQuotableResorts = (resorts, contractType) => resorts
  .filter((resort) => (contractType === 'refinance' ? resort.refinanceQuotesConfigured : resort.purchaseQuotesConfigured))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * Loan terms, in years, that can be selected for a resort. Without a resort,
 * returns the terms available at any resort.
 * @param {object} config API config with resorts and supportedTerms
 * @param {object|null} resort Selected resort
 * @returns {number[]}
 */
export const getAvailableTerms = (config, resort) => {
  const resorts = resort ? [resort] : config.resorts;
  const maxTerm = Math.max(0, ...resorts.map(({ maxLoanTermYears }) => Number(maxLoanTermYears) || 0));

  const supportedTerms = resort && Array.isArray(resort.supportedTerms) ? resort.supportedTerms : config.supportedTerms;

  return supportedTerms
    .map(Number)
    .filter((term) => Number.isInteger(term) && term >= 1 && term <= maxTerm)
    .sort((a, b) => a - b);
};

/**
 * Terms to label on the slider: the last term of each interest rate tier, as
 * reported by the API, plus the longest available term. Falls back to every
 * fourth year when the API doesn't report rate tiers.
 * @param {object|null} resort Selected resort
 * @param {number[]} terms Available terms in ascending order
 * @returns {number[]}
 */
export const getLabeledTerms = (resort, terms) => {
  if (!terms.length) {
    return [];
  }

  const tierEnds = resort && Array.isArray(resort.rateTierEndTerms)
    ? resort.rateTierEndTerms.map(Number)
    : terms.filter((term) => term % 4 === 0);

  return terms.filter((term) => tierEnds.includes(term) || term === terms[terms.length - 1]);
};

/**
 * Snap a term to the closest available term that does not exceed it
 * @param {number} term Requested term in years
 * @param {number[]} terms Available terms in ascending order
 * @returns {number|null}
 */
export const clampTerm = (term, terms) => {
  if (!terms.length) {
    return null;
  }

  const allowed = terms.filter((t) => t <= Number(term));

  return allowed.length ? allowed[allowed.length - 1] : terms[0];
};

const CREDIT_RATINGS = [600, 650, 700, 800];
const LOAN_OPTIONS = [1, 2, 3];

/**
 * Config query for the loan terms these quote inputs qualify for, or null
 * until enough inputs are entered. The API picks each resort's interest table
 * from them, so a quote that misses a table's minimums can get shorter terms.
 * @param {object} value Context value
 * @returns {object|null}
 */
export const getEligibilityQuery = (value) => {
  const totalPoints = Number(value.totalPoints);

  if (!Number.isInteger(totalPoints) || totalPoints < 1 || totalPoints > 4000) {
    return null;
  }

  if (value.contractType === 'refinance') {
    const loanOption = Number(value.downPaymentOption);
    const creditRating = Number(value.creditRating);
    const query = { loanPurpose: 'refinance', totalPoints, loanOption: LOAN_OPTIONS.includes(loanOption) ? loanOption : 1 };

    if (CREDIT_RATINGS.includes(creditRating)) {
      query.creditRating = creditRating;
    }

    return query;
  }

  // The same product the API prices, so both agree at the minimum
  const purchasePrice = totalPoints * Number(value.pricePerPoint);

  if (!Number.isFinite(purchasePrice) || purchasePrice <= 0 || purchasePrice > 4000000) {
    return null;
  }

  return { loanPurpose: 'purchase', totalPoints, purchasePrice };
};

/**
 * Each resort's eligible maximum term and rate tiers from a config response
 * that was given quote inputs
 * @param {object} data Config response data
 * @returns {object} Keyed by resort code
 */
export const getEligibleResorts = (data) => (Array.isArray(data.resorts) ? data.resorts : [])
  .reduce((resorts, { code, maxLoanTermYears, rateTierEndTerms, supportedTerms, fallbackNotes }) => {
    resorts[code] = { maxLoanTermYears, rateTierEndTerms, supportedTerms, fallbackNotes };

    return resorts;
  }, {});

/**
 * The resort with its term limits for the latest eligibility check. Without
 * eligible terms (not checked yet, or none qualify) the configured limits stay,
 * so the API can explain the problem with the quote itself.
 * @param {object} value Context value
 * @param {object|null} resort Resort from the API config
 * @returns {object|null}
 */
export const withEligibleTerms = (value, resort) => {
  const eligible = resort && value.eligibility && value.eligibility.resorts[resort.code];

  return eligible && Number(eligible.maxLoanTermYears) > 0 ? { ...resort, ...eligible } : resort;
};

/**
 * The inputs the API sizes the no-credit-check slider amounts from: down
 * payments from the contract price, refinance amounts from the points'
 * liquidation value. Choosing a different option doesn't change them.
 * @param {object} value Context value
 * @returns {string}
 */
export const getSliderOptionsKey = (value) => JSON.stringify([
  value.contractType,
  value.resort,
  String(value.totalPoints || ''),
  value.contractType === 'new' ? String(value.pricePerPoint || '') : '',
]);

/**
 * Amounts to label a no-credit-check slider with, or null while they are
 * unknown or were calculated for inputs that have since changed
 * @param {object} value Context value
 * @param {string} kind Either 'downPayment' or 'refinance'
 * @returns {number[]|null}
 */
export const getSliderOptions = (value, kind) => {
  const saved = value.sliderOptions;
  const options = saved && saved.key === getSliderOptionsKey(value) ? saved[kind] : null;

  return Array.isArray(options) && options.length ? options : null;
};


// Public notes are plain text. Ignore malformed values and repeated messages.
export const normalizeFallbackNotes = (notes) => [...new Set(
  (Array.isArray(notes) ? notes : [])
    .filter((note) => typeof note === 'string')
    .map((note) => note.trim())
    .filter(Boolean)
)];

/** Notes for the current inputs, never a previous eligibility check or quote. */
export const getFallbackNotes = (value) => {
  const response = value.response || {};
  if (response.error && Array.isArray(response.fallbackNotes) && response.fallbackNotes.length) {
    return normalizeFallbackNotes(response.fallbackNotes);
  }
  const quotes = [response.noCreditCheck];
  if (Number(value.creditRating) > -1) quotes.push(response.creditCheck);
  const currentQuotes = quotes.filter((quote) => quote && Array.isArray(quote.fallback_notes));
  if (currentQuotes.length) {
    return normalizeFallbackNotes(currentQuotes.reduce((notes, quote) => notes.concat(quote.fallback_notes), []));
  }
  const resort = findResort(value.config.resorts, value.resort);
  if (!resort) return [];
  const query = getEligibilityQuery(value);
  const eligible = query && value.eligibility && value.eligibility.key === JSON.stringify(query)
    ? value.eligibility.resorts[resort.code] : null;
  return normalizeFallbackNotes(eligible ? eligible.fallbackNotes : resort.fallbackNotes);
};



// WEBPACK FOOTER //
// blocks/quote-calculator/app/loanConfig.js